import type { ServerMessage } from '../shared/protocol.ts';

type Timer = ReturnType<typeof setTimeout> | number;

export type ScreenConnection = 'connecting' | 'connected' | 'reconnecting' | 'access-required';

/** A socket is ready only after the relay accepts it and supplies a snapshot. */
export function connectDisplay(options: {
  url: string;
  token: string;
  viewport: string;
  onStatus: (status: ScreenConnection) => void;
  onMessage: (message: ServerMessage) => boolean;
  socket?: (url: string) => WebSocket;
  schedule?: (fn: () => void, ms: number) => Timer;
  cancel?: (timer: Timer) => void;
}) {
  const makeSocket = options.socket ?? ((url: string) => new WebSocket(url));
  const schedule = options.schedule ?? setTimeout;
  const cancel = options.cancel ?? clearTimeout;
  let current: WebSocket | undefined;
  let retry: Timer | undefined;
  let attempt = 0;
  let stopped = false;
  function connect() {
    if (stopped) return;
    const socket = makeSocket(options.url);
    current = socket;
    let synced = false;
    const active = () => !stopped && current === socket;
    socket.onopen = () => {
      if (!active()) return;
      if (options.token) socket.send(JSON.stringify({type: 'viewer.auth', token: options.token}));
      socket.send(JSON.stringify({type: 'panel.hello', viewport: options.viewport}));
    };
    socket.onmessage = e => {
      if (!active()) return;
      let message: ServerMessage;
      try { message = JSON.parse(e.data as string); } catch { return; }
      if (!message || typeof message !== 'object') return;
      if (message.type === 'sync') {
        if (!message.state || !Number.isInteger(message.seq)) { socket.close(); return; }
        synced = true;
        attempt = 0;
        options.onStatus('connected');
      } else if (!synced) {
        socket.close(); return;
      }
      if (!options.onMessage(message)) socket.close();
    };
    socket.onclose = e => {
      if (!active()) return;
      current = undefined;
      if (e.code === 4401 || e.code === 4403) {
        options.onStatus('access-required');
        return; // Repeating the same rejected credential cannot repair access.
      }
      options.onStatus('reconnecting');
      retry = schedule(connect, Math.min(5000, 300 * 2 ** attempt++));
    };
    socket.onerror = () => { if (active()) socket.close(); };
  }
  options.onStatus('connecting');
  connect();
  return () => {
    stopped = true;
    if (retry !== undefined) cancel(retry);
    current?.close();
  };
}
